import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { MediumText, RegularText } from '../../lib/Text';
import { useEffect, useMemo, useState } from 'react';
import SvgIcon from '@/components/SvgIcon';
import { colors } from '@/lib/colors';
import ModalCenterReportPermission from './ModalCenterReportPermission';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import CoachReportModal from '../OpsBriefing/CoachReportModal';
import { useCoachReportAccess } from '../OpsBriefing/useCoachReportAccess';

type CenterReportTab = {
    label: string;
    path: string;
    isNew?: boolean;
};

const baseTabs: CenterReportTab[] = [
    { label: '리뷰 통계', path: '/centerReport/instructorReviews' },
    { label: '센터 통계', path: '/centerReport/centerStatistics' },
    { label: '회원 리뷰', path: '/centerReport/memberReviews' },
    // { label: '회원 만족도', path: '/centerReport/memberSatisfaction' },
];

export const CenterReportLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isNewOpportunityEnabled, isLoading: isFeatureFlagsLoading } = useCenterFeatureFlags();
    const tabs = useMemo(
        () =>
            isNewOpportunityEnabled
                ? [...baseTabs, { label: '신규기회 분석', path: '/centerReport/newOpportunities', isNew: true }]
                : baseTabs,
        [isNewOpportunityEnabled]
    );
    const isActiveScreen = (path: string) => location.pathname.includes(path);
    const isNewOpportunityScreen = location.pathname.includes('/centerReport/newOpportunities');
    const [isReviewPermissionModalOpen, setIsReviewPermissionModalOpen] = useState(false);
    /*
     * 강사 성과 리포트 — 운영 브리핑 상단 버튼과 같은 팝업(강사 고르기부터).
     * 데이터는 팝업이 열릴 때 CoachReportModal / CoachReportSheet 가 직접 받아온다.
     * 이 레이아웃은 열고 닫는 것만 안다.
     */
    const canOpenCoachReport = useCoachReportAccess();
    const [isCoachReportOpen, setIsCoachReportOpen] = useState(false);
    // const [isReviewEndModalOpen, setIsReviewEndModalOpen] = useState(false);

    // 리뷰 권한 모달 여부
    // 첫 진입시 첫 번째 탭으로 리디렉트
    useEffect(() => {
        if (location.pathname === '/centerReport') {
            navigate(tabs[0].path, { replace: true });
        }
    }, [location.pathname, navigate]);

    useEffect(() => {
        if (!isFeatureFlagsLoading && !isNewOpportunityEnabled && isNewOpportunityScreen) {
            navigate('/centerReport/memberReviews', { replace: true });
        }
    }, [isFeatureFlagsLoading, isNewOpportunityEnabled, isNewOpportunityScreen, navigate]);

    // ModalReviewEndPopup 표시 여부 확인
    // useEffect(() => {
    //     const hasSeenModal = getItemWithExpiration('isReviewEndModalShown');
    //     if (!hasSeenModal) {
    //         setIsReviewEndModalOpen(true);
    //     }
    // }, []);

    // 모달을 닫을 때 localStorage에 표시 완료 상태 저장
    // const handleCloseReviewEndModal = () => {
    //     setItemWithExpiration('isReviewEndModalShown', true, 60 * 60 * 24 * 365); // 1년간 저장
    //     setIsReviewEndModalOpen(false);
    // };

    return (
        <div className="flex flex-col items-center w-full h-full bg-[#F4F5F7]">
            <div className="w-full max-w-[1440px] ">
                {/* 탭 UI */}
                <div
                    className="flex w-full items-center justify-between gap-[16px]"
                    style={{ marginTop: 108 }}
                >
                    <div className="flex flex-wrap items-center gap-[6px] rounded-[16px] border border-solid border-[#E2E8F0] bg-white p-[6px] shadow-[0_8px_24px_rgba(26,48,93,0.06)]">
                        {tabs.map((tab) => {
                            const isActive = isActiveScreen(tab.path);
                            return (
                                <button
                                    type="button"
                                    className="h-[44px] rounded-[11px] px-[22px] transition-colors"
                                    key={tab.path}
                                    onClick={() => navigate(tab.path)}
                                    style={{
                                        border: 'none',
                                        background: isActive ? '#1A305D' : 'transparent',
                                        cursor: 'pointer',
                                        boxShadow: isActive ? '0 6px 16px rgba(26,48,93,0.18)' : 'none',
                                        position: 'relative',
                                    }}
                                >
                                    <MediumText
                                        fontSize={18}
                                        lineHeight={24}
                                        color={isActive ? '#FFFFFF' : '#8B95A5'}
                                        letterSpacing={0}
                                        className="cursor-pointer whitespace-nowrap"
                                    >
                                        {tab.label}
                                    </MediumText>
                                    {tab.isNew && (
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: -7,
                                                right: 8,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                borderRadius: 999,
                                                background: '#EF4444',
                                                color: '#FFFFFF',
                                                fontSize: 9,
                                                lineHeight: '12px',
                                                fontWeight: 800,
                                                padding: '1px 5px',
                                                boxShadow: '0 2px 6px rgba(239,68,68,0.35)',
                                            }}
                                        >
                                            NEW
                                        </span>
                                    )}
                                </button>
                            );
                            // 선택한 화면에 따라 스타일 변경
                        })}
                    </div>
                    <div className="flex flex-none items-center gap-[10px]">
                        {/*
                          강사 성과 리포트 — 면담용 A4 3장.

                          원장이 센터 통계를 보다가 "그럼 이 강사는?"으로 넘어가는 자리가
                          여기입니다. 운영 브리핑 탭까지 옮겨 가야만 뽑을 수 있으면
                          그 화면에 있을 때만 생각납니다.

                          ⚠️ 노출 게이트가 닫힌 센터에서는 **조용히 사라집니다.**
                             리포트가 쓰는 조회는 전부 게이트 안이라, 눌리면 404 입니다.
                        */}
                        {canOpenCoachReport && (
                            <button
                                type="button"
                                aria-label="강사 성과 리포트 열기"
                                onClick={() => setIsCoachReportOpen(true)}
                                className="flex h-[44px] flex-none flex-row items-center rounded-[11px] border-none px-[20px] shadow-[0_8px_20px_rgba(26,48,93,0.22)]"
                                style={{ background: '#1A305D', cursor: 'pointer', gap: 8 }}
                            >
                                <span style={{ fontSize: 16 }}>📄</span>
                                <RegularText
                                    fontSize={16}
                                    lineHeight={21}
                                    color={'#FFFFFF'}
                                    letterSpacing={-0.5}
                                    className="cursor-pointer whitespace-nowrap"
                                >
                                    강사 성과 리포트
                                </RegularText>
                            </button>
                        )}
                        {!isNewOpportunityScreen && (
                            <div
                                className="flex-row justify-center items-center flex bg-white w-[200px] h-[44px] rounded-[11px] border border-solid border-[#E2E8F0] cursor-pointer shadow-[0_8px_20px_rgba(26,48,93,0.05)]"
                                onClick={() => setIsReviewPermissionModalOpen(true)}
                            >
                                <SvgIcon name="SvgSetting" size={17} fill={colors.Text_1} />
                                <RegularText
                                    fontSize={16}
                                    lineHeight={21}
                                    color={'#42484D'}
                                    letterSpacing={-0.5}
                                    className="ml-[8px]"
                                >
                                    강사별 수업 리뷰 설정
                                </RegularText>
                            </div>
                        )}
                    </div>
                </div>

                {/* 중첩된 페이지들 */}
                <div style={{ marginTop: isNewOpportunityScreen ? 28 : 64 }}>
                    <Outlet />
                </div>
            </div>
            {isReviewPermissionModalOpen && (
                <ModalCenterReportPermission setIsVisible={setIsReviewPermissionModalOpen} />
            )}
            {isCoachReportOpen && <CoachReportModal onClose={() => setIsCoachReportOpen(false)} />}
            {/* {isReviewEndModalOpen && <ModalReviewEndPopup setIsVisible={handleCloseReviewEndModal} />} */}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </div>
    );
};
